import type { Region } from "./types";

/** Single config: add a new region here = ONE file change for the whole UI. */
export const REGION_COLORS: Record<Region, { hex: string; tailwind: string }> = {
  EUROPE:        { hex: "#3b82f6", tailwind: "bg-blue-500" },
  MIDDLE_EAST:   { hex: "#f43f5e", tailwind: "bg-rose-500" },
  AFRICA:        { hex: "#16a34a", tailwind: "bg-green-600" },
  NORTH_AMERICA: { hex: "#7c3aed", tailwind: "bg-violet-600" },
  LATIN_AMERICA: { hex: "#0891b2", tailwind: "bg-cyan-600" },
  ASIA_PACIFIC:  { hex: "#f97316", tailwind: "bg-orange-500" },
  // Legacy fallbacks aligned to modern equivalents
  EMEA:          { hex: "#3b82f6", tailwind: "bg-blue-500" },
  ASIA:          { hex: "#f97316", tailwind: "bg-orange-500" },
  LATAM:         { hex: "#0891b2", tailwind: "bg-cyan-600" },
};

export const REGION_COLOR_DEFAULT = { hex: "#e8e6e1", tailwind: "bg-text-dim" };

/**
 * djb2 hash → deterministic colour from a fixed 8-slot palette.
 * Guarantees any unknown region string gets a vivid, non-grey colour.
 * Same input always produces the same output (stable across re-renders).
 */
function _hashColor(s: string): string {
  const PALETTE = [
    "#6366f1", "#ec4899", "#14b8a6", "#f59e0b",
    "#84cc16", "#f97316", "#8b5cf6", "#0ea5e9",
  ];
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return PALETTE[Math.abs(h) % PALETTE.length];
}

/**
 * Returns a hex or tailwind colour for a region value.
 *
 * - Known regions (REGION_COLORS): exact match, correct palette entry.
 * - Unknown/new region string: deterministic hash colour — NEVER invisible grey.
 * - null/undefined: default grey (no region data).
 */
export function getRegionColor(
  region: Region | string | null | undefined,
  format: "hex" | "tailwind" = "hex",
): string {
  if (!region) {
    return format === "hex" ? REGION_COLOR_DEFAULT.hex : REGION_COLOR_DEFAULT.tailwind;
  }
  const c = REGION_COLORS[region as Region];
  if (c) return c[format];
  // Unknown region — deterministic hash colour (never invisible grey).
  // tailwind class not available for hash colours; return hex for both formats.
  return _hashColor(region);
}

/** All canonical region keys in display order. Used by dropdowns. */
export const ALL_REGIONS: Region[] = [
  "EUROPE",
  "MIDDLE_EAST",
  "AFRICA",
  "NORTH_AMERICA",
  "LATIN_AMERICA",
  "ASIA_PACIFIC",
];

/** Deprecated regions kept for backward compat — not shown in new-country dropdowns. */
export const DEPRECATED_REGIONS: Region[] = ["EMEA", "ASIA", "LATAM"];
